import type { DebtSlip, VatInvoice } from '../types';

export interface MatchOptions {
  tolerancePct: number;
  toleranceAbs: number;
  dateWindowDays: number;
  matchAmount: number; // số tiền dùng để khớp (mặc định = totalPayable)
}

export interface MatchCandidate {
  invoice: VatInvoice;
  score: number; // 0..1
  amountDiff: number;
  nameSim: number;
  amountOk: boolean;
  taxOk: boolean;
  dateDiff: number;
  reasons: string[];
}

export interface AutomaticMatchPolicy {
  minScore: number;
  minGap: number;
  minNameSimilarity: number;
}

export interface AutomaticMatchDecision {
  match?: MatchCandidate;
  reason?: string;
}

export function normalizeName(s?: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // bỏ dấu tiếng Việt
    .replace(/đ/g, 'd')
    .replace(/\b(cong ty|cty|tnhh|mtv|cp|tm|npp|nha phan phoi|co ?ltd)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function nameSimilarity(a?: string, b?: string): number {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return 0;
  const sa = new Set(na.split(' ').filter(Boolean));
  const sb = new Set(nb.split(' ').filter(Boolean));
  if (!sa.size || !sb.size) return 0;
  const inter = [...sa].filter((x) => sb.has(x)).length;
  const uni = new Set([...sa, ...sb]).size;
  return inter / uni; // Jaccard
}

function daysBetween(a?: string, b?: string): number {
  if (!a || !b) return 99999;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (isNaN(da) || isNaN(db)) return 99999;
  return Math.abs(da - db) / 86400000;
}

const digits = (s?: string) => (s || '').replace(/\D/g, '');

export function findMatches(
  slip: DebtSlip,
  invoices: VatInvoice[],
  opts: MatchOptions,
): MatchCandidate[] {
  const target = opts.matchAmount;
  const out: MatchCandidate[] = [];

  for (const inv of invoices) {
    const reasons: string[] = [];
    let score = 0;

    // 1) Số tiền (trọng số cao nhất)
    const amountDiff = Math.abs((inv.amount || 0) - target);
    const tol = Math.max(opts.toleranceAbs, (target * opts.tolerancePct) / 100);
    const amountOk = target > 0 && amountDiff <= tol;
    if (amountOk) {
      score += 0.5 * (1 - amountDiff / (tol || 1));
      reasons.push('Khớp số tiền');
    }

    // 2) MST khớp tuyệt đối
    const taxOk = !!digits(slip.taxCode) && digits(slip.taxCode) === digits(inv.taxCode);
    if (taxOk) {
      score += 0.3;
      reasons.push('Khớp MST');
    }

    // 3) Tên NCC gần khớp
    const nameSim = nameSimilarity(slip.supplierName, inv.supplierName);
    score += 0.2 * nameSim;
    if (nameSim > 0.4) reasons.push('Tên NCC gần khớp');

    // 4) Ngày trong cửa sổ
    const dd = daysBetween(slip.slipDate, inv.invoiceDate);
    if (dd <= opts.dateWindowDays) {
      score += 0.1;
      reasons.push('Ngày trong khoảng');
    }

    if (amountOk || taxOk || nameSim > 0.4) {
      out.push({ invoice: inv, score: Math.min(1, score), amountDiff, nameSim, amountOk, taxOk, dateDiff: dd, reasons });
    }
  }

  return out.sort((a, b) => b.score - a.score);
}

export function selectAutomaticMatch(
  candidates: MatchCandidate[],
  policy: AutomaticMatchPolicy,
): AutomaticMatchDecision {
  const first = candidates[0];
  if (!first) return { reason: 'Không tìm thấy hóa đơn phù hợp.' };
  if (first.score < policy.minScore) return { reason: 'Điểm đối soát chưa đạt ngưỡng tự động.' };
  if (!first.amountOk) return { reason: 'Số tiền hóa đơn không nằm trong dung sai.' };
  if (!first.taxOk && first.nameSim < policy.minNameSimilarity) {
    return { reason: 'Nhà cung cấp không khớp MST và tên chưa đủ tương đồng.' };
  }
  const second = candidates[1];
  if (second && first.score - second.score < policy.minGap) {
    return { reason: 'Có nhiều hóa đơn gần điểm nhau, cần người dùng xác nhận.' };
  }
  return { match: first };
}
